/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareLinkedEntity, userSupportedTypes } from '../fw/MetaDataHelper.ts';
import * as Permission from '../util/Permission';
import { ACCESS_TYPE_DEVELOPER, ACCESS_TYPE_PARTNER1, ACCESS_TYPE_PARTNER2, ACCESS_TYPE_REGULARUSER } from './UserType';

export const publicExcludedKeys = [
    "id",
    "dataType",
    "filter",
    "order",
    "orderType",
    "dataTypeForOne",
    "listAddon",
    "sourceDefKey",
    "urlFragment",
    "page",
    "$root",
    "page1",
    "page2",
    "since",
    "offset",
    "offset1",
    "offset2",
    "limit",
    "timestampMiliSec",
    "timestampSec",
    "timestampDateIso",
    "dativeryUserId",
    "parent",
    "parentId",
    "organisation_id",
    "channel_id",
    "requestUrl",
    "requestBody",
    "requestMethod",
    "isAuthTest",
    "actionOperation",
    "linkServer",
    "locale",
];

export const excludedKeys = [
    ...publicExcludedKeys,

    "timestamp", // now deprecated
    "oauthAccessToken",
    "oauthRefreshToken",
];

export const ID_QUERY_MEANING_JSONPATH = "jsonpath";
export const ID_QUERY_MEANING_XPATH = "xpath";
export const ID_QUERY_MEANING_VARIABLE = "variable";

export const CONFIG_STEP_1 = "1";
export const CONFIG_STEP_2 = "2";
export const CONFIG_STEP_3 = "3";

export default declareLinkedEntity({
    entity: "channelOptionDefs",
    graphQL: "ChannelOptionDef",
    name: "Application Option Definition",
    defaultOrder: ['order', 'name'],
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    dynamoPrefix: 'def',
    storageType: 'schema',
    parentRelation: 'channelOptionDefs',
    defaultTitle: (data) => {
        return data['name'];
    },
    onModify: ['channelDef'],
    fields: (types) => { return {
        id: {
            name: "Application Option Definition ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            compositeKey: true,
            userEditable: false,
        },
        channelDef: {
            name: "Application Definition",
            type: "relation",
            to: () => types['channelDefs'],
            defaultValue: (channelDef) => {
                return channelDef['id'];
            },
            required: true,
            parent: true,
            compositeKey: true,
            hiddenCreate: true,
        },
        channelDefVersion: {
            name: "Channel Definition Version",
            to: () => types['channelDefVersions'],
            type: "relation",
            defaultValue: (channelDef) => {
                return channelDef['channelDefVersion_id'];
            },
            required: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        order: {
            name: "Order",
            type: "int",
            defaultValue: "0",
        },
        required: {
            name: "Required",
            type: "boolean",
            defaultValue: true,
            visibility: (data) => { return !data['disabled'] && !data['internal']; },
        },
        disabled: {
            name: "Disabled",
            type: "boolean",
            defaultValue: false,
        },
        internal: {
            name: "Internal",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return !data['disabled']; },
        },
        key: {
            name: "Key",
            type: "string",
            trim: true,
            defaultValue: "",
            required: true,
            validations: {
                matchRegexp: /^[a-zA-Z0-9_]+$/,
            }
        },
        name: {
            name: "Name",
            type: "string",
            trim: true,
            translatable: true,
            defaultValue: "",
            required: true,
        },
        prefix: {
            name: "Prefix",
            type: "string",
            trim: true,
            translatable: true,
            description: "It is part of input field and suggest what user should add (eg. https://[user entered data].example.com/).",
            defaultValue: "",
        },
        suffix: {
            name: "Suffix",
            type: "string",
            trim: true,
            translatable: true,
            description: "It is part of input field and suggest what user should add (eg. https://[user entered data].example.com/).",
            defaultValue: "",
        },
        examples: {
            name: "Examples",
            type: "string",
            translatable: true,
            trim: true,
            defaultValue: "",
            visibility: (data) => { return !data['internal']; },
        },
        defaultValue: {
            name: "Default Value",
            type: "string",
            translatable: true,
            trim: true,
            defaultValue: "",
            visibility: (data) => { return !data['internal']; },
        },
        defaultValueForExisting: {
            name: "Default Value for existing apps",
            type: "string",
            translatable: true,
            trim: true,
            defaultValue: "",
            visibility: (data) => { return !data['internal']; },
        },
        description: {
            name: "Description",
            type: "text",
            translatable: true,
            defaultValue: ""
        },
        step: {
            name: "Step",
            type: "enum",
            defaultValue: CONFIG_STEP_1,
            values: {
                [CONFIG_STEP_1]: {
                    name: "First",
                },
                [CONFIG_STEP_2]: {
                    name: "Second",
                },
                [CONFIG_STEP_3]: {
                    name: "Third (after grant)",
                },
            },
            visibility: (data) => { return !data['internal']; },
        },
        isAuthIdentity: {
            name: "Is authentication identity",
            type: "boolean",
            description: "Is this part of user authorization?",
            defaultValue: false,
        },
        isAuthInfo: {
            name: "Is authentication info",
            type: "boolean",
            description: "Should this option be stored in safe store for authentication data",
            defaultValue: false,
            visibility: (data) => { return data['isAuthIdentity'] || data['isAuthInfo']; }
        },
        skipOnCreate: {
            name: "Skip on channel create",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return !data['disabled'] && !data['internal']; },
        },
        accessType: {
            name: "Access Type",
            type: "enum",
            visibility: (data) => { return !data['disabled'] && !data['internal']; },
            values: {
                [ACCESS_TYPE_REGULARUSER]: {
                    name: "Regular User",
                },
                [ACCESS_TYPE_PARTNER1]: {
                    name: "Partner 1",
                },
                [ACCESS_TYPE_PARTNER2]: {
                    name: "Partner 2",
                },
                [ACCESS_TYPE_DEVELOPER]: {
                    name: "Developers Only",
                },
            },
            defaultValue: ACCESS_TYPE_REGULARUSER,
        },
        developersOnly: {
            name: "Visible only for developers",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return !data['disabled'] && !data['internal']; }
        },
        partner1Only: {
            name: "Visible only for Partner 1",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return !data['disabled'] && !data['internal']; },
        },
        partner2Only: {
            name: "Visible only for Partner 2",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return !data['disabled'] && !data['internal']; },
        },
        type: {
            name: "Type",
            type: "enum",
            defaultValue: "string",
            values: userSupportedTypes,
        },
        javascript: {
            name: "JavaScript",
            type: "javascript",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return data['type'] === 'js-action'; },
            globalVariables: {
                'options': 'options: Map',
                'currentUserOptions': 'currentUserOptions: Map',
                'setOptions': 'setOptions(newOptions: Map<string,any>)',
                'setAuthOptions': 'setAuthOptions(newOptions: Map<string,any>)',
                'setCurrentUserOptions': 'setCurrentUserOptions(newOptions: Map<string,any>)',
                'download': 'download(content, fileName: string)',
                'channel': 'ChannelType',
                'channelDef': 'ChannelDefType',
            },
        },
        visibilityJS: {
            name: "Visibility JS",
            type: "javascript",
            defaultValue: "",
            globalVariables: {
                'getValue': 'getValue(field: string)',
                'setTitle': 'setTitle(title: string)',
                'hasChannelDef': 'hasChannelDef(keyOrChannelDefId: string, alias: string, noExtended?: boolean)',
            },
            visibility: (data) => { return !data['internal']; },
        },
        filterValuesJS: {
            name: "Filter Values JS",
            type: "javascript",
            defaultValue: "",
            globalVariables: {
                'getValue': 'getValue(field: string)',
                'json': 'json: Map<String,String>',
            },
            visibility: (data) => { return (data['type'] === 'enum' || data['type'] === 'enums' || data['type'] === 'dictionary') && false; },
        },
        regexp: {
            name: "RegExp Validation",
            description: "Validation must contain ^$. Example: ^[a-zA-Z0-9\-]+$",
            type: "string",
            defaultValue: "",
        },
        fileContentTypes: {
            name: "File content types",
            type: "string",
            visibility: (data) => { return data['type'] === 'file' || data['type'] === 'certificate' || data['type'] === 'image'; }
        },
        datiQuery: {
            name: "DatiQuery",
            type: "string",
            visibility: (data) => { return data['type'] === 'enum'; },
            descriptionHtml: "<small>Example: <pre>SELECT * FROM [APP $CURRENT TYPE \"eu.flexibee.c\" ADDING]</pre></small>",
        },
        datiQueryHasParameters: {
            name: "DatiQuery has parameters",
            type: "boolean",
            visibility: (data) => { return data['type'] === 'enum'; },
        },
        enumValues: {
            name: "Enum Values",
            type: "object",
            visibility: (data) => { return data['type'] === 'enum'; },
            descriptionHtml: "<small>Example: <pre>{    \"CZ\": {\n        \"name\": {\n            \"en\": \"Czech\",\n            \"cs\": \"Česko\"\n        }\n    },\n    \"EN\": {\n        \"name: {\n            \"en\": \"English\",\n            \"cs\": \"Česky\"\n        }\n    }}</pre></small>",
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        }
    }; },
});
